import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Star, Vote, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { FourStars, StarsDivider } from "../components/FourStars";
import { toast } from "sonner";

export default function PollsPage() {
  const { user } = useAuth();
  const [polls, setPolls] = useState([]);
  const [voting, setVoting] = useState(null);

  const load = async () => {
    const { data } = await api.get("/polls");
    setPolls(data);
  };

  useEffect(() => { load(); }, [user]);

  const submit = async (poll_id, option_id) => {
    if (!user) {
      toast.error("Connectez-vous pour voter.");
      return;
    }
    setVoting(poll_id);
    try {
      await api.post(`/polls/${poll_id}/vote`, { option_id });
      toast.success("Vote enregistré !");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du vote");
    } finally {
      setVoting(null);
    }
  };

  return (
    <div data-testid="polls-page">
      <section className="relative py-20 md:py-28 text-white overflow-hidden bg-gradient-to-br from-[#0A0F1A] via-[#0052CC] to-[#0A0F1A]">
        <div className="absolute inset-0 stars-pattern opacity-15" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
          <FourStars size={14} className="justify-center mb-4" />
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Sondages & Élections</div>
          <h1 className="font-display text-4xl md:text-6xl mt-3 leading-tight">Votre voix construit l'avenir</h1>
          <p className="text-white/80 mt-5 max-w-2xl mx-auto leading-relaxed">
            Participez aux consultations citoyennes et élections internes A.F.B.
          </p>
          {!user && (
            <Button asChild size="lg" className="mt-7 bg-[#D4AF37] hover:bg-[#B5952F] text-[#0A0F1A] rounded-sm">
              <Link to="/login"><Lock size={16} className="mr-2" /> Connectez-vous pour voter</Link>
            </Button>
          )}
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-10 space-y-6">
          {polls.map((p, idx) => (
            <div key={p.id} className="bg-white rounded-2xl border border-black/5 shadow-sm p-7 md:p-9" data-testid={`public-poll-${idx}`}>
              <div className="flex items-start gap-3">
                <Star size={20} fill="#D4AF37" stroke="#D4AF37" className="mt-1" />
                <div className="flex-1">
                  <h2 className="font-display text-2xl text-[#0A0F1A]">{p.question}</h2>
                  <p className="text-sm text-[#475569] mt-1">{p.description}</p>
                </div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#475569]">{p.total_votes} votes</div>
              </div>
              <div className="space-y-2.5 mt-6">
                {p.options.map((o) => {
                  const pct = p.total_votes ? Math.round((o.votes / p.total_votes) * 100) : 0;
                  const voted = p.user_voted === o.option_id;
                  return (
                    <button
                      key={o.option_id}
                      onClick={() => submit(p.id, o.option_id)}
                      disabled={voting === p.id || !user}
                      data-testid={`pub-poll-${idx}-opt-${o.option_id}`}
                      className={`w-full text-left relative overflow-hidden rounded-md border-2 transition-all ${
                        voted ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-black/10 hover:border-[#0052CC]/40"
                      } ${!user ? "cursor-not-allowed opacity-80" : ""}`}
                    >
                      <div className="absolute inset-y-0 left-0 bg-[#0052CC]/10" style={{ width: `${pct}%` }} />
                      <div className="relative flex items-center justify-between px-5 py-3.5">
                        <span className="text-sm font-medium text-[#0A0F1A]">{o.label}</span>
                        <span className="text-sm font-mono text-[#475569]">{pct}% · {o.votes}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="text-xs text-[#475569] mt-5">
                Clôture le {new Date(p.closes_at).toLocaleDateString("fr-FR", { dateStyle: "long" })}
              </div>
            </div>
          ))}
          {polls.length === 0 && (
            <div className="py-16 text-center text-sm text-[#475569]">Aucun sondage en cours.</div>
          )}
        </div>
      </section>
      <StarsDivider />
    </div>
  );
}
